import { Check, CheckCheck, Forward, Pin, Plus, Star } from 'lucide-react';
import { groupInitials, type GroupSummary } from '../../../lib/group-chat';
import { seenByLabel, seenByStack, type GroupReceiptMember, type TickState } from '../../../lib/group-receipts';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { Avatar } from '../ui';
import { chipA11y, GROUP_QUICK_REACTIONS, type PinBanner, type ReactionChip } from '../../../lib/group-message-actions';
import { useTranslation } from '../../../lib/i18n';
import { Button, IconButton } from '../ui';

const TICK_LABEL_KEYS: Record<TickState, string> = { sent: 'chat.group.tick.sent', delivered: 'chat.group.tick.delivered', read: 'chat.group.tick.read' };

/**
 * Design §21: WhatsApp's ticks beside the time on the viewer's own group row — ✓ sent (grey), ✓✓ delivered (grey),
 * ✓✓ read (accent). iOS / Android `GroupTicks` draw the same.
 */
export function GroupTicks({ state }: { state: TickState }) {
  const { t } = useTranslation();
  const Icon = state === 'sent' ? Check : CheckCheck;
  return (
    <span data-testid={`group-tick-${state}`} role="img" aria-label={t(TICK_LABEL_KEYS[state])}
      style={{ display: 'inline-flex', verticalAlign: 'middle', marginInlineStart: 4, color: state === 'read' ? 'var(--accent-solid)' : 'currentColor' }}>
      <Icon size={14} strokeWidth={2} absoluteStrokeWidth aria-hidden />
    </span>
  );
}

const SEEN_SIZE = 16;
const SEEN_STEP = 10;

/**
 * Design §21: "Seen by" — a small right-aligned row of overlapping avatars (up to three, then "+N") under the newest
 * row a person has read. Its label names every one ("Seen by Caro, Alex"). iOS / Android `GroupSeenBy` draw the same.
 */
export function GroupSeenBy({ members, inset }: { members: readonly GroupReceiptMember[]; inset?: boolean }) {
  const { t } = useTranslation();
  const { shown, plus } = seenByStack(members);
  const label = seenByLabel(members, t);
  return (
    <div data-testid="group-seen-by" role="img" aria-label={label} title={label}
      style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 2, marginBottom: 4, paddingInlineEnd: inset ? 40 : 0, width: '100%', boxSizing: 'border-box' }}>
      <span style={{ position: 'relative', display: 'inline-block', height: SEEN_SIZE, width: SEEN_SIZE + SEEN_STEP * (shown.length - 1) }}>
        {shown.map((m, i) => (
          <span key={m.metaSiteId} style={{ position: 'absolute', top: 0, insetInlineStart: i * SEEN_STEP, zIndex: i + 1, borderRadius: '50%', outline: '1.5px solid var(--group-ring, var(--ds-surface-standard-bg))' }}>
            <Avatar size={SEEN_SIZE} initials={groupInitials(m.name)} photoUrl={m.avatarUrl} color={avatarSlotFor(m.metaSiteId)} />
          </span>
        ))}
      </span>
      {plus > 0 && <span aria-hidden style={{ fontSize: 11, lineHeight: '16px', color: 'var(--ds-text-secondary)' }}>+{plus}</span>}
    </div>
  );
}
import reactionStyles from '../chat-surface/MessageRow/MessageReactions.module.css';

/**
 * Group chat Phase 10b — what a group bubble wears: the "Forwarded" label, "Edited" and the star by the time, the
 * reaction chips, and the quick reaction row. The decisions are `lib/group-message-actions.ts`; MessageRow places
 * these. iOS `GroupMessageParts` / Android `GroupMessageParts` draw the same.
 */

/** What MessageRow needs to draw a group row's Phase 10b parts (the panel decides each). */
export interface GroupRowExtras {
  chips: ReactionChip[];
  /** A chip tapped: the who-reacted sheet on that emoji. */
  onChip?: (emoji: string) => void;
  /** The quick row's pick (the panel turns it into set / replace / clear). */
  onReact?: (emoji: string) => void;
  /** The quick row's "+": the full emoji picker. */
  onMoreReactions?: () => void;
  /** The viewer's current reaction (the quick row shows it pressed). */
  mine: string | null;
  forwarded: boolean;
  edited: boolean;
  starred: boolean;
  summary: GroupSummary;
  /** Design §21: the ticks on the viewer's own row (absent on every other row). */
  tick?: TickState;
}

export function GroupForwardedLabel() {
  const { t } = useTranslation();
  return (
    <div data-testid="group-forwarded" style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, fontSize: 12, lineHeight: '16px', fontStyle: 'italic', color: 'var(--ds-text-secondary)' }}>
      <Forward size={12} strokeWidth={1.75} absoluteStrokeWidth aria-hidden />
      <span>{t('chat.group.message.forwarded')}</span>
    </div>
  );
}

/** "Edited" and the star, beside the time (inline, so they sit on the time's line). */
export function GroupTimeBadges({ edited, starred }: { edited: boolean; starred: boolean }) {
  const { t } = useTranslation();
  if (!edited && !starred) return null;
  return (
    <>
      {starred && (
        <span data-testid="group-starred-badge" role="img" aria-label={t('chat.group.message.starredA11y')} style={{ display: 'inline-flex', verticalAlign: 'middle', marginInlineEnd: 4 }}>
          <Star size={11} strokeWidth={1.75} absoluteStrokeWidth fill="currentColor" aria-hidden />
        </span>
      )}
      {edited && <span data-testid="group-edited" style={{ marginInlineEnd: 4 }}>{t('chat.group.message.edited')}</span>}
    </>
  );
}

/** The reaction chips under a group bubble: the emoji, its count when more than one; mine highlighted. */
export function GroupReactionChips({ chips, summary, onChip, overlapBubble = false, inset = 0 }: { chips: ReactionChip[]; summary: GroupSummary; onChip?: (emoji: string) => void; overlapBubble?: boolean; inset?: number }) {
  const { t } = useTranslation();
  if (chips.length === 0) return null;
  return (
    <div data-testid="group-reaction-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: overlapBubble ? -6 : 4, ...(overlapBubble ? { marginInlineStart: 12, marginInlineEnd: 12 } : {}), ...(inset ? { paddingInlineStart: inset, paddingInlineEnd: inset } : {}) }}>
      {chips.map((c) => (
        <Button
          key={c.emoji}
          variant="outlined"
          color="standard"
          size="tiny"
          onClick={onChip ? () => onChip(c.emoji) : undefined}
          aria-pressed={c.mine}
          aria-label={chipA11y(c, summary, t)}
          className={c.mine ? `${reactionStyles.chip} ${reactionStyles.mine}` : reactionStyles.chip}
        >
          <span aria-hidden>{c.emoji}</span>
          {c.count > 1 && <span style={{ marginInlineStart: 4, fontSize: '0.71875rem', fontWeight: 'var(--sym-weight-semibold)', opacity: 0.7 }}>{c.count}</span>}
        </Button>
      ))}
    </div>
  );
}

/** The quick reaction row: 👍 ❤️ 😂 😮 😢 🙏 and a "+" for the full picker. The viewer's current one reads pressed. */
export function GroupQuickReactBar({ mine, onPick, onMore }: { mine: string | null; onPick: (emoji: string) => void; onMore?: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('chat.group.message.react')} data-testid="group-quick-react" style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {GROUP_QUICK_REACTIONS.map((emoji) => (
        <IconButton key={emoji} size="small" variant="text" onClick={() => onPick(emoji)} aria-label={emoji} aria-pressed={mine === emoji} title={emoji}
          style={mine === emoji ? { background: 'var(--ds-surface-accent)' } : undefined}>
          <span aria-hidden style={{ fontSize: '1rem', lineHeight: 1 }}>{emoji}</span>
        </IconButton>
      ))}
      {onMore && (
        <IconButton size="small" variant="text" onClick={onMore} aria-label={t('chat.group.message.moreReactions')} title={t('chat.group.message.moreReactions')}>
          <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      )}
    </div>
  );
}

/**
 * The pinned-message banner at the top of a group chat: "Pinned message" (or "… {index} of {count}", 1 = the newest)
 * and the pin's first line ("Attachment" without text). A tap jumps to that message; the panel then advances it.
 */
export function GroupPinnedBanner({ banner, onOpen, topInset = 0 }: { banner: PinBanner; onOpen: () => void; topInset?: number }) {
  const { t } = useTranslation();
  const label = t(banner.labelKey, banner.vars);
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="group-pinned-banner"
      aria-label={`${label}: ${banner.text ?? t('chat.group.pinned.attachment')}. ${t('chat.group.pinned.next')}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', boxSizing: 'border-box', width: '100%',
        // Above the transcript's edge fade; on a phone-width page, below the floating top bar.
        position: 'relative', zIndex: 4, flexShrink: 0, marginTop: topInset,
        borderBottom: '1px solid rgba(var(--hairline-rgb),0.16)', background: 'var(--sym-bg-elevated)',
      }}
    >
      <Pin size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden style={{ flexShrink: 0, color: 'var(--accent-solid)' }} />
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 12, lineHeight: '16px', fontWeight: 'var(--sym-weight-semibold)', color: 'var(--accent-solid)' }}>{label}</span>
        <span dir="auto" style={{ fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {banner.text ?? t('chat.group.pinned.attachment')}
        </span>
      </span>
    </div>
  );
}
